/**
 * Pure geometry core — ported from PSET AccessibleWashroomSizeCheck.js
 * (measureClearAABB). Model-agnostic; castRay is injected.
 */
import type { CastRayFn, ClearAabbResult } from "./types";

export interface MeasureClearAabbOptions {
  rays?: number;
  maxDist: number;
  marchStep: number;
  bounces?: number;
  minHits?: number;
}

export function measureClearAABB(
  origin: THREE.Vector3,
  castRay: CastRayFn,
  options: MeasureClearAabbOptions
): ClearAabbResult | null {
  const { rays = 72, maxDist, marchStep, bounces = 16, minHits = 3 } = options;

  const march = (dir: THREE.Vector3): THREE.Vector3 | null => {
    let cur = origin.clone();
    for (let k = 0; k < bounces; k++) {
      const h = castRay(cur, dir);
      if (!h || h.point.distanceTo(origin) > maxDist) return null;
      if (h.kind === "boundary") return h.point;
      if (h.kind === "opening") return null;
      cur = h.point.clone().add(dir.clone().multiplyScalar(marchStep));
    }
    return null;
  };

  const pts: THREE.Vector3[] = [];
  for (let i = 0; i < rays; i++) {
    const th = (i / rays) * 2 * Math.PI;
    const p = march(new THREE.Vector3(Math.cos(th), Math.sin(th), 0));
    if (p) pts.push(p);
  }
  if (pts.length < minHits) return null;

  let mnx = Number.POSITIVE_INFINITY;
  let mny = Number.POSITIVE_INFINITY;
  let mxx = Number.NEGATIVE_INFINITY;
  let mxy = Number.NEGATIVE_INFINITY;
  for (const p of pts) {
    mnx = Math.min(mnx, p.x);
    mxx = Math.max(mxx, p.x);
    mny = Math.min(mny, p.y);
    mxy = Math.max(mxy, p.y);
  }
  const box = new THREE.Box3(
    new THREE.Vector3(mnx, mny, origin.z),
    new THREE.Vector3(mxx, mxy, origin.z)
  );
  return { width: mxx - mnx, depth: mxy - mny, box, points: pts };
}
